import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMapStore } from '../../../store/useMapStore';
import { RadioReceiver, Play, Pause, Activity, Music, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import { useTranslation } from '../../../i18n/translations';

const parseTime = (timeStr: string) => {
  const [time, modifier] = timeStr.split(' ');
  let [hours, minutes] = time.split(':');
  let h = parseInt(hours, 10);
  if (modifier === 'PM' && h < 12) h += 12;
  if (modifier === 'AM' && h === 12) h = 0;
  const d = new Date();
  d.setHours(h, parseInt(minutes, 10), 0, 0);
  return d.getTime();
};

export const RadioStationCard: React.FC = () => {
  const { 
    selectedRadioStation, 
    radioData,
    setSelectedRadioStation,
    isRadioPlaying,
    setIsRadioPlaying,
    theme 
  } = useMapStore();
  const { t } = useTranslation();

  const [cuesheets, setCuesheets] = useState<Record<string, any[]>>({});
  const [currentTime, setCurrentTime] = useState<number>(Date.now());
  const [expandedStationId, setExpandedStationId] = useState<string | null>(null);

  useEffect(() => {
    fetch('/data/radio/cuesheets.json')
      .then(res => res.json())
      .then(data => setCuesheets(data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(Date.now()), 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  const isDark = theme === 'dark';
  const textColor = isDark ? '#f8fafc' : '#0f172a';
  const subTextColor = isDark ? '#94a3b8' : '#64748b';
  const borderColor = isDark ? '#334155' : '#e2e8f0';

  if (!selectedRadioStation || !radioData) return null;

  // Group by city to find all stations in the selected city
  const city = selectedRadioStation.properties.city;
  const cityStations = radioData.features
    .filter(f => f.properties.city === city)
    .sort((a, b) => (a.properties.name || '').localeCompare(b.properties.name || ''));

  if (cityStations.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      style={{
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginTop: '0px',
        overflow: 'hidden',
        border: `1px solid ${borderColor}`,
        width: '100%'
      }}
      className="glass result-card"
    >
      <div style={{
        padding: '16px',
        borderBottom: `1px solid ${borderColor}`,
        background: isDark ? '#0f172a' : '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <div style={{ 
          background: 'rgba(236, 72, 153, 0.15)', 
          padding: '8px', 
          borderRadius: '10px',
          color: '#ec4899'
        }}>
          <RadioReceiver size={20} />
        </div>
        <div>
          <h4 style={{ margin: 0, color: textColor, fontSize: '1.05rem', fontWeight: 700 }}>
            Akashvani {city}
          </h4>
          <span style={{ fontSize: '0.8rem', color: subTextColor }}>
            {cityStations.length} {t('STATIONS' as any) || 'Stations'} Available
          </span>
        </div>
      </div>

      <div style={{ padding: '8px' }}>
        {cityStations.map((station, idx) => {
          const isSelected = selectedRadioStation.properties.id === station.properties.id;
          const isPlaying = isSelected && isRadioPlaying;
          const isExpanded = expandedStationId === station.properties.id;
          
          const schedule = cuesheets[station.properties.id] || [];
          const currentIndex = schedule.findIndex(p => {
            const start = parseTime(p.start);
            const end = parseTime(p.end);
            return currentTime >= start && currentTime < end;
          });
          
          const currentProgram = currentIndex !== -1 ? schedule[currentIndex] : null;

          return (
            <div
              key={station.properties.id || idx}
              style={{
                margin: '4px',
                borderRadius: '12px',
                background: isSelected ? (isDark ? 'rgba(236, 72, 153, 0.15)' : 'rgba(236, 72, 153, 0.1)') : 'transparent',
                border: `1px solid ${isSelected ? 'rgba(236, 72, 153, 0.4)' : 'transparent'}`,
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  if (isSelected) {
                    setIsRadioPlaying(!isRadioPlaying);
                  } else {
                    setSelectedRadioStation(station);
                    setIsRadioPlaying(true);
                  }
                }}
              >
                {/* Animated Equalizer when playing */}
                <div style={{ width: '32px', display: 'flex', justifyContent: 'center' }}>
                  {isPlaying ? (
                    <Activity size={20} color="#ec4899" className="animate-pulse" />
                  ) : (
                    <span style={{ 
                      fontSize: '0.85rem', 
                      fontWeight: 700, 
                      color: isSelected ? '#ec4899' : subTextColor 
                    }}>
                      {station.properties.freq}
                    </span>
                  )}
                </div>

                <div style={{ flex: 1, marginLeft: '12px', overflow: 'hidden' }}>
                  <div style={{ 
                    fontWeight: 600, 
                    color: isSelected ? (isDark ? '#fbcfe8' : '#be185d') : textColor,
                    fontSize: '0.95rem'
                  }}>
                    {station.properties.name}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: subTextColor, marginTop: '2px' }}>
                    {station.properties.freq} MHz
                  </div>
                  {currentProgram && !isExpanded && (
                    <div 
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedStationId(isExpanded ? null : station.properties.id);
                      }}
                      style={{ 
                        fontSize: '0.75rem', 
                        color: isDark ? '#38bdf8' : '#0284c7', 
                        marginTop: '4px',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      <Music size={12} style={{ flexShrink: 0 }} />
                      <div style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {currentProgram.program}
                      </div>
                      <ChevronDown size={14} style={{ flexShrink: 0 }} />
                    </div>
                  )}
                </div>

                <button
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: isSelected ? '#ec4899' : (isDark ? '#334155' : '#f1f5f9'),
                    color: isSelected ? '#fff' : (isDark ? '#f8fafc' : '#0f172a'),
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    flexShrink: 0,
                    marginLeft: '8px'
                  }}
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isSelected) {
                      setIsRadioPlaying(!isRadioPlaying);
                    } else {
                      setSelectedRadioStation(station);
                      setIsRadioPlaying(true);
                    }
                  }}
                >
                  {isPlaying ? <Pause size={16} /> : <Play size={16} style={{ marginLeft: '2px' }} />}
                </button>
              </div>

              {/* Expandable Timeline */}
              <AnimatePresence>
                {isExpanded && schedule.length > 0 && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    style={{ overflow: 'hidden' }}
                  >
                    <div style={{
                      padding: '8px 16px 16px 44px',
                      borderTop: `1px solid ${borderColor}40`,
                      position: 'relative'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px'
                      }}>
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: subTextColor, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Clock size={12} /> Today's Schedule
                        </div>
                        <button
                          onClick={() => setExpandedStationId(null)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: subTextColor,
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <ChevronUp size={16} />
                        </button>
                      </div>

                      <div style={{ position: 'relative', paddingLeft: '16px', borderLeft: `2px solid ${borderColor}` }}>
                        {schedule.slice(Math.max(0, currentIndex - 1), currentIndex + 3).map((prog, i) => {
                          const isCurrent = prog === currentProgram;
                          const isPast = schedule.indexOf(prog) < currentIndex;
                          
                          return (
                            <div key={i} style={{ position: 'relative', marginBottom: '16px' }}>
                              {/* Timeline dot */}
                              <div style={{
                                position: 'absolute',
                                left: '-21px',
                                top: '4px',
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                background: isCurrent ? '#ec4899' : (isPast ? borderColor : (isDark ? '#475569' : '#cbd5e1')),
                                border: `2px solid ${isDark ? '#1e293b' : '#ffffff'}`,
                                zIndex: 2
                              }} />
                              
                              <div style={{
                                fontSize: '0.7rem',
                                color: isCurrent ? '#ec4899' : subTextColor,
                                fontWeight: isCurrent ? 700 : 500,
                                marginBottom: '2px'
                              }}>
                                {prog.start} - {prog.end}
                                {isCurrent && <span style={{ marginLeft: '6px', background: 'rgba(236, 72, 153, 0.1)', padding: '2px 6px', borderRadius: '4px' }}>Now Playing</span>}
                              </div>
                              <div style={{
                                fontSize: '0.8rem',
                                color: isPast ? subTextColor : textColor,
                                fontWeight: isCurrent ? 600 : 400,
                                lineHeight: '1.4'
                              }}>
                                {prog.program}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {currentIndex + 3 < schedule.length && (
                        <div style={{ fontSize: '0.75rem', color: subTextColor, textAlign: 'center', marginTop: '8px', fontStyle: 'italic' }}>
                          + {schedule.length - (currentIndex + 3)} more programs today
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};
